import { useMutation, useQuery, useQueryClient } from 'react-query';
import axiosInstance from '../config/Axios';
import { useCustomToast } from '../pages/Contexts/ToastContext';

/**
 * Content Library — standalone, reusable video-wall playlists, decoupled from any
 * single wall (Phase 1 of the restaurant-chain platform redesign; backend
 * PlaylistLibraryController). A later phase assigns a library playlist to walls.
 *
 *   GET    /playlists        → { playlists } (with items_count)
 *   POST   /playlists        create (name, description, items)
 *   GET    /playlists/{id}    → { playlist } (+ items)
 *   PUT    /playlists/{id}    replace name / description / items
 *   DELETE /playlists/{id}
 *
 * Backend wraps responses in a Laravel resource ({ data: { status, ... } });
 * hooks unwrap with `res?.data ?? res`. (Distinct from queries/playlists.js,
 * which handles campaign playlists.)
 */

const LIST_KEY = 'wallPlaylistLibrary';
const itemKey = (id) => ['wallPlaylist', id];

export const useGetPlaylists = () =>
  useQuery(
    LIST_KEY,
    async () => {
      const res = (await axiosInstance.get('/playlists')).data;
      return (res?.data ?? res)?.playlists ?? [];
    },
    { staleTime: 15_000 }
  );

export const useGetPlaylist = (id, enabled = true) =>
  useQuery(
    itemKey(id),
    async () => {
      const res = (await axiosInstance.get(`/playlists/${id}`)).data;
      return (res?.data ?? res)?.playlist ?? null;
    },
    { enabled: !!id && enabled, staleTime: 5_000 }
  );

export const useCreatePlaylist = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation((payload) => axiosInstance.post('/playlists', payload), {
    onSuccess: () => {
      qc.invalidateQueries(LIST_KEY);
      showToast('success', 'Content Library', 'Playlist created.');
    },
    onError: (err) => showToast('error', 'Content Library', err.response?.data?.message),
  });
};

export const useUpdatePlaylist = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation(({ id, ...payload }) => axiosInstance.put(`/playlists/${id}`, payload), {
    onSuccess: (_res, vars) => {
      qc.invalidateQueries(LIST_KEY);
      qc.invalidateQueries(itemKey(vars.id));
      showToast('success', 'Content Library', 'Playlist updated.');
    },
    onError: (err) => showToast('error', 'Content Library', err.response?.data?.message),
  });
};

export const useDeletePlaylist = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation((id) => axiosInstance.delete(`/playlists/${id}`), {
    onSuccess: () => {
      qc.invalidateQueries(LIST_KEY);
      showToast('info', 'Content Library', 'Playlist deleted.');
    },
    onError: (err) => showToast('error', 'Content Library', err.response?.data?.message),
  });
};
